'use client';

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import { useRouter } from 'next/navigation';
import { useDebounce } from '@/hooks/use-debounce';
import { useLocale, useLocalePath } from '@/hooks/use-locale';
import {
  COMPANY_SUGGEST_MIN_CHARS,
  useCompanySuggest,
  type CompanySuggestion,
} from '@/hooks/use-company-suggest';
import { trackHomepageSearch } from '@/lib/analytics';
import { generateCompanySlug } from '@/lib/company-utils';

/**
 * Search box behaviour shared by the hero search and the header search:
 * debounced suggestions and keyboard selection. Submitting opens a company
 * only when it was picked with the arrow keys, otherwise the full search, as
 * in LandingSearch: a list that opens under the cursor highlights a row by
 * hover alone. The two boxes render their own markup.
 */
interface Highlight {
  list: CompanySuggestion[] | undefined;
  index: number;
  byKeyboard: boolean;
}
const NO_HIGHLIGHT: Highlight = { list: undefined, index: -1, byKeyboard: false };

export function useCompanySearchBox(value: string, listId: string) {
  const router = useRouter();
  const { locale } = useLocale();
  const localePath = useLocalePath();
  const debounced = useDebounce(value, 180);
  const { data, isFetching } = useCompanySuggest(debounced, 8);
  const [open, setOpen] = useState(false);
  // A highlight belongs to the result list it was made in; new results clear it.
  const [sel, setSel] = useState<Highlight>(NO_HIGHLIGHT);
  const formRef = useRef<HTMLFormElement>(null);

  const long = value.trim().length >= COMPANY_SUGGEST_MIN_CHARS;
  const hits: CompanySuggestion[] = long && data ? data : [];
  const current = sel.list === data;
  const active = current ? sel.index : -1;
  const byKeyboard = current && sel.byKeyboard;

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (formRef.current && !formRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const openCompany = useCallback(
    (c: CompanySuggestion) => {
      setOpen(false);
      trackHomepageSearch(c.name.trim());
      router.push(
        generateCompanySlug(
          {
            name: c.name,
            registerCourt: c.registerCourt,
            registerType: c.registerType,
            registerId: c.registerId,
          },
          locale,
        ),
      );
    },
    [router, locale],
  );

  const searchAll = useCallback(() => {
    const q = value.trim();
    setOpen(false);
    if (q) trackHomepageSearch(q);
    router.push(
      q
        ? `${localePath('/search')}?q=${encodeURIComponent(q)}&sort=relevance-desc`
        : localePath('/search'),
    );
  }, [router, localePath, value]);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (open && byKeyboard && active >= 0 && hits[active]) openCompany(hits[active]);
    else searchAll();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      if (hits.length) setSel({ list: data, index: (active + 1) % hits.length, byKeyboard: true });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (hits.length) setSel({ list: data, index: active <= 0 ? hits.length - 1 : active - 1, byKeyboard: true });
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const showList = open && long;
  const empty = showList && !isFetching && debounced.trim() === value.trim() && hits.length === 0;

  return {
    formRef,
    hits,
    active,
    hover: (i: number) => setSel({ list: data, index: i, byKeyboard: false }),
    showList,
    empty,
    open: () => setOpen(true),
    close: () => setOpen(false),
    openCompany,
    searchAll,
    onSubmit,
    inputProps: {
      role: 'combobox' as const,
      'aria-autocomplete': 'list' as const,
      'aria-controls': listId,
      'aria-expanded': showList,
      'aria-activedescendant': showList && active >= 0 ? `${listId}-${active}` : undefined,
      onKeyDown,
      onFocus: () => setOpen(true),
    },
  };
}
